// Trajet d'un match à l'extérieur (D-053) : depuis notre gymnase, arriver `arrive` minutes
// avant le match (1 h par défaut), d'où l'heure de départ et le rendez-vous au gymnase
// (`meet` minutes avant le départ). Le temps de route est arrondi aux 5 minutes du dessus :
// on préfère attendre 3 minutes sur le parking que d'arriver en retard. Logique pure.

export interface Place { lat: number; lng: number; label: string | null }
export interface CachedTrip { km: number; min: number; from: [number, number]; to: [number, number] }
export interface TripInfo {
  away: boolean; starts_at: string;
  to: Place | null; from: Place | null;
  arrive_min: number; meet_min: number;
  trip: CachedTrip | null;
}

export const roundUp5 = (min: number) => Math.max(5, Math.ceil(min / 5) * 5);

/** Arrivée, départ et rendez-vous, à partir du coup d'envoi. */
export function schedule(start: Date, travelMin: number, arriveMin: number, meetMin: number) {
  const arrive = new Date(start.getTime() - arriveMin * 60_000);
  const depart = new Date(arrive.getTime() - roundUp5(travelMin) * 60_000);
  const meet = new Date(depart.getTime() - meetMin * 60_000);
  return { arrive, depart, meet };
}

/** « 42 min », « 1 h 05 ». */
export function duration(min: number): string {
  const m = roundUp5(min);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}

/** « 1 h », « 45 min », « 1 h 30 » (pour les réglages d'avance). */
export const advance = (min: number) => (min % 60 === 0 && min ? `${min / 60} h` : min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`);

const near = (a: [number, number], b: [number, number]) => Math.abs(a[0] - b[0]) < 1e-4 && Math.abs(a[1] - b[1]) < 1e-4;

/** Le temps de route gardé sur le match vaut-il encore (même départ, même salle) ? */
export const cacheValid = (t: TripInfo): t is TripInfo & { trip: CachedTrip; from: Place; to: Place } =>
  !!t.trip && !!t.from && !!t.to && Array.isArray(t.trip.from) && Array.isArray(t.trip.to)
  && near(t.trip.from, [t.from.lat, t.from.lng]) && near(t.trip.to, [t.to.lat, t.to.lng]);

/** L'itinéraire dans l'application de cartes du téléphone. */
export const mapsUrl = (from: Place, to: Place) =>
  `https://www.google.com/maps/dir/?api=1&origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&travelmode=driving`;

/** La salle d'une rencontre FFBB (coordonnées et adresse de la cartographie FFBB). */
export function venueOf(salle: {
  libelle?: string | null; adresse?: string | null; commune?: { libelle?: string | null } | null;
  cartographie?: { latitude?: number | null; longitude?: number | null; codePostal?: string | null; ville?: string | null } | null;
} | null | undefined): Place | null {
  const c = salle?.cartographie;
  const lat = Number(c?.latitude), lng = Number(c?.longitude);
  if (!c || !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  const town = c.ville || salle?.commune?.libelle || null;
  const label = [salle?.libelle, town].filter(Boolean).join(', ').slice(0, 200) || null;
  return { lat, lng, label };
}
